/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * HistoryPage.tsx — Histor\1 \2age (services/operator-web-app).
 */

/**
 * History: list of past calls from backend GET /api/calls.
 * E4-S1: Integrated with web-ui backend; filters by status and search.
 */
import { useEffect, useState } from 'react';
import { fetchCallsList, type CallListItem, type CallsListResponse } from '@/api';
import { ApiError } from '@/api/client';
import { useTranslation } from '@/i18n';

/** Formats a backend nanosecond timestamp with the active browser locale. */
function formatStartTime(ts: number): string {
  if (!ts) return '—';
  const d = new Date(ts / 1e6);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Formats a millisecond duration as minutes and zero-padded seconds. */
function formatDuration(ms: number | undefined): string {
  if (ms == null || ms < 0) return '—';
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function HistoryPage() {
  const t = useTranslation();
  const [data, setData] = useState<CallsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCallsList({
      status: statusFilter || undefined,
      search: search.trim() || undefined,
      limit: 100,
      offset: 0,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.message || t('history.error', { status: err.status }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, search, t]);

  /** Resolves a backend call state without translating unknown diagnostic values. */
  const statusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      active: t('history.status_active'),
      ended: t('history.status_ended'),
      bridged: t('history.status_bridged'),
    };
    return labels[status] ?? status;
  };

  return (
    <div className="py-6 space-y-6">
      <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
        {t('history.title')}
      </h1>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          placeholder={t('history.search_placeholder')}
          className="rounded border px-3 py-1.5 text-sm w-64"
          style={{
            borderColor: 'var(--glass-border)',
            background: 'var(--toggle-bg)',
            color: 'var(--text-primary)',
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('history.search_label')}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded border px-3 py-1.5 text-sm"
          style={{
            borderColor: 'var(--glass-border)',
            background: 'var(--toggle-bg)',
            color: 'var(--text-primary)',
          }}
          aria-label={t('history.status_filter')}
        >
          <option value="">{t('history.all_statuses')}</option>
          <option value="active">{t('history.status_active')}</option>
          <option value="ended">{t('history.status_ended')}</option>
          <option value="bridged">{t('history.status_bridged')}</option>
        </select>
      </div>

      {error && (
        <div
          className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('history.loading')}
        </p>
      )}

      {!loading && !error && data && (
        <>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {t('history.total', { count: data.total })}
          </p>
          {data.calls.length === 0 ? (
            <section
              className="rounded-lg border p-6 text-center glass-panel"
              aria-label={t('history.list_label')}
            >
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {t('history.empty')}
              </p>
            </section>
          ) : (
            <div
              className="overflow-x-auto rounded-lg border"
              style={{ borderColor: 'var(--glass-border)' }}
            >
              <table className="min-w-full text-sm text-left">
                <thead style={{ background: 'var(--toggle-bg)', color: 'var(--text-secondary)' }}>
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('history.time')}</th>
                    <th className="px-4 py-2 font-medium">Call ID</th>
                    <th className="px-4 py-2 font-medium">Caller / Callee</th>
                    <th className="px-4 py-2 font-medium">{t('history.duration')}</th>
                    <th className="px-4 py-2 font-medium">{t('history.status')}</th>
                    <th className="px-4 py-2 font-medium">{t('history.scenario')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
                  {data.calls.map((call: CallListItem) => (
                    <tr key={call.call_id} style={{ background: 'var(--glass-bg-from)' }}>
                      <td
                        className="px-4 py-2 whitespace-nowrap"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {formatStartTime(call.start_time)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{call.call_id}</td>
                      <td className="px-4 py-2">
                        {call.caller_id ?? '—'} / {call.callee_id ?? '—'}
                      </td>
                      <td className="px-4 py-2">{formatDuration(call.duration_ms)}</td>
                      <td className="px-4 py-2">{statusLabel(call.status)}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>
                        {call.scenario_name ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
